import { Modal } from 'antd';
import { Button, Icon } from 'hyarchis-global';
import React, { useCallback, useState } from 'react';
import { CrossMatchResult, DataMatchGroup } from 'types/proccesingJobs';
import { DataRow, MatchType, RowValue } from 'types/documentGroup';
import Styled from '../listItemHead.styled';
import GroupListMatchForm from './GroupListMatchForm';

const { HeadIconWrapper } = Styled;

interface OwnProps {
  groupName: string;
  jobMatches: CrossMatchResult;
}

interface MatchIndice {
  index: number;
  matchType: MatchType;
}

interface RejectedRow {
  rowIndex: number;
  rejectReason: string;
}

const GroupListMatchButton = ({ groupName, jobMatches }: OwnProps) => {
  const mapMatchIndices = (matchGroup: DataMatchGroup): MatchIndice[] => {
    const results: MatchIndice[] = [];

    matchGroup.dataSetReferenceIndices.forEach((indice) => {
      const result: MatchIndice = { index: indice, matchType: matchGroup.matchType };
      results.push(result);
    });

    return results;
  };

  const getRowStatus = (rowValues: RowValue[], rowIndex: number) => {
    if (allRejectedRows.find((row) => rowIndex === row.rowIndex) !== undefined) {
      return MatchType.NoMatch;
    }

    if (allAcceptedRows.find((row) => rowIndex === row) !== undefined) {
      return MatchType.FullMatch;
    }

    if (rowValues.find((row) => row.matchType === MatchType.PartialMatch)) {
      return MatchType.PartialMatch;
    }

    if (rowValues.find((row) => row.matchType === MatchType.NoMatch)) {
      return MatchType.NoMatch;
    }

    return MatchType.FullMatch;
  };

  const createDataRow = (row: string, rowIndex: number): DataRow => {
    const filledDataRow: DataRow = { key: row, rowValues: [] };

    uniqueDocumentTypes.forEach((column, index) => {
      if (index > 0) {
        const indexOfMatchingSet = dataSets.findIndex(
          (data) => data.dataType === row && data.documentDataSource.documentType === column,
        );
        const matchType = matchingIndices.find((indice) => indice.index === indexOfMatchingSet);

        if (indexOfMatchingSet < 0 || !matchType) {
          filledDataRow.rowValues.push({ matchType: MatchType.NotApplicable, value: 'N/A' });
          return;
        }

        const rowValue = {
          matchType: matchType.matchType,
          value: dataSets[indexOfMatchingSet].value,
        };

        filledDataRow.rowValues.push(rowValue);
      } else {
        filledDataRow.rowValues.push({ matchType: MatchType.NotApplicable, value: row });
      }
    });
    const rowStatus = getRowStatus(filledDataRow.rowValues, rowIndex);
    filledDataRow.rowValues.push({ matchType: rowStatus, value: '' });

    const matchingRejectedRow = allRejectedRows.find(
      (rejectedRow) => rowIndex === rejectedRow.rowIndex,
    );
    if (matchingRejectedRow) {
      filledDataRow.rowValues.push({
        matchType: MatchType.NotApplicable,
        value: matchingRejectedRow.rejectReason,
      });
    }

    return filledDataRow;
  };

  const getModalWidth = (documentTypesCount: number) => {
    const maxWidth = 1800;
    const columnSize = 300;
    const width = documentTypesCount * columnSize + columnSize;

    if (width > maxWidth) {
      return maxWidth;
    }

    return width;
  };

  const onAcceptRowsAdd = (rowIndex: number) => {
    setAcceptRows((rows) => {
      rows.push(rowIndex);
      return rows;
    });

    setDataRowsChange(() => uniqueTypes.map(createDataRow));
  };

  const onRejectRowsAdd = (rowIndex: number, rejectReason = '') => {
    const rejectedRow: RejectedRow = { rowIndex, rejectReason };
    setRejectRows((rows) => {
      const existingRow = rows.find((row) => row.rowIndex === rowIndex);
      if (existingRow) {
        existingRow.rejectReason = rejectReason;
      } else {
        rows.push(rejectedRow);
      }

      return rows;
    });

    setDataRowsChange(() => uniqueTypes.map(createDataRow));
  };

  const modalTitle = `Match results of group: ${groupName}`;
  const dataSets = jobMatches.dataset;
  const dataTypes = dataSets.map((data) => data.dataType);
  const documentTypes = dataSets.map((data) => data.documentDataSource.documentType);

  const uniqueDocumentTypes = ['Extraction type', ...new Set(documentTypes)];

  const matchingIndices = jobMatches.matches.map((match) => mapMatchIndices(match)).flat();

  const uniqueTypes = [...new Set(dataTypes)];

  const acceptedRows: number[] = [];
  const rejectedRows: RejectedRow[] = [];
  const [allAcceptedRows, setAcceptRows] = useState(acceptedRows);
  const [allRejectedRows, setRejectRows] = useState(rejectedRows);

  const dataRows: DataRow[] = uniqueTypes.map(createDataRow);

  const [isModalOpen, setOpen] = useState(false);
  const [mappedDataRows, setDataRowsChange] = useState(dataRows);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <HeadIconWrapper>
      <Button
        icon={<Icon icon='clipboardList' type='regular' size={24} />}
        size='large'
        type='text'
        onClick={() => setOpen(true)}
      />
      <Modal
        title={modalTitle}
        width={getModalWidth(uniqueDocumentTypes.length)}
        okText='OK'
        visible={isModalOpen}
        onCancel={closeModal}
        onOk={closeModal}
      >
        <GroupListMatchForm
          columns={uniqueDocumentTypes}
          rows={mappedDataRows}
          onAcceptRow={onAcceptRowsAdd}
          onRejectRow={onRejectRowsAdd}
          onRejectReasonAdd={onRejectRowsAdd}
        />
      </Modal>
    </HeadIconWrapper>
  );
};

export default GroupListMatchButton;
